import { FormEvent, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useSignupMutation,
  useGetPublicCyclesStatsQuery,
  useResendVerificationMutation,
} from "@/store/api";
import { useAppSelector } from "@/store";
import { EquityCurve } from "@/components/charts/EquityCurve";
import { DailyPnL } from "@/components/charts/DailyPnL";
import { toast } from "sonner";

export default function Signup() {
  const token = useAppSelector((s) => s.auth.token);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [signup, { isLoading }] = useSignupMutation();
  const [resend, { isLoading: resending }] = useResendVerificationMutation();
  const demo = useGetPublicCyclesStatsQuery(200);

  if (token) return <Navigate to="/" replace />;

  // Only show "Real trading performance" once the prod track record is
  // actually profitable — don't market a losing curve to prospective users.
  // Sum net P&L across Cycles closes; require strictly > 0
  // before any chart renders. Loading state hides too (avoids a flash).
  const sumNetPnl = (rows: unknown[]): number =>
    rows.reduce<number>(
      (acc, r) => acc + Number((r as { net_pnl_usd?: number | null })?.net_pnl_usd ?? 0),
      0,
    );
  const cyclesPnl = demo.data ? sumNetPnl(demo.data as unknown[]) : 0;
  const totalPnl = cyclesPnl;
  const showPerformance =
    !demo.isLoading && totalPnl > 0;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!acceptedTerms) return setError("You must accept the Terms of Service.");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords don't match.");
    try {
      const res = await signup({ email, password, accepted_terms: true }).unwrap();
      setSubmittedEmail(res.email);
      toast.success("Check your inbox to verify your email.");
    } catch (e) {
      const msg = (e as { data?: { detail?: string } })?.data?.detail ?? "Signup failed";
      setError(msg);
    }
  };

  const onResend = async () => {
    if (!submittedEmail) return;
    try {
      await resend({ email: submittedEmail }).unwrap();
      toast.success("Verification email re-sent. Check your inbox (and spam).");
    } catch {
      toast.error("Couldn't re-send right now. Try again in a minute.");
    }
  };

  return (
    <div className="min-h-screen bg-bg text-fg p-4 lg:p-10 space-y-8">
      <div className="grid lg:grid-cols-2 gap-6 lg:gap-10">
      {/* Pitch */}
      <div className="max-w-xl space-y-5 lg:pt-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs bg-accent/10 text-accent border border-accent/20">
          <span className="w-2 h-2 rounded-full bg-accent" /> AI-managed Binance Spot
        </div>
        <h1 className="text-3xl lg:text-5xl font-semibold leading-tight">
          Let our AI trade your Binance spot account.
          <br />
          <span className="text-accent">Pay only when you profit — 10%.</span>
        </h1>
        <p className="text-muted text-base leading-relaxed">
          We run a Gemini-backed strategy on every cycle. You set your own
          Binance API keys and your own order limits — the bot only ever
          spends within those caps. Approve or auto-execute the trades the
          AI proposes for your account.
        </p>
        <ul className="space-y-2 text-sm text-fg/90">
          <li className="flex gap-2"><span className="text-accent">✓</span> Your Binance keys stay on your account — encrypted at rest.</li>
          <li className="flex gap-2"><span className="text-accent">✓</span> You set the per-order USD cap and the daily order limit.</li>
          <li className="flex gap-2"><span className="text-accent">✓</span> Manual-approve every trade, or flip to auto when you're comfortable.</li>
          <li className="flex gap-2"><span className="text-accent">✓</span> <strong>10% of realized profit only.</strong> Losses cost zero performance fee.</li>
          <li className="flex gap-2"><span className="text-accent">✓</span> Live ROI chart, full trade history, fee ledger.</li>
        </ul>
        <p className="text-xs text-muted">
          Already have an account? <Link to="/login" className="text-accent underline">Sign in</Link>
        </p>
      </div>

      {/* Form */}
      <div className="grid place-items-center lg:place-items-end">
        {submittedEmail ? (
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Check your email</CardTitle>
              <CardDescription>
                We sent a verification link to{" "}
                <strong className="text-fg">{submittedEmail}</strong>. Click
                the link in that email to activate your account — you can't
                sign in or trade until then.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted">
                The link expires in 24 hours. Look in spam if you don't see it
                within a minute.
              </p>
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                disabled={resending}
                onClick={onResend}
              >
                {resending ? "Re-sending…" : "Re-send verification email"}
              </Button>
              <p className="text-xs text-muted">
                Wrong email? <Link to="/signup" className="text-accent underline" onClick={() => setSubmittedEmail(null)}>Start over</Link>
              </p>
            </CardContent>
          </Card>
        ) : (
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Create your account</CardTitle>
            <CardDescription>
              Free to start. You'll add your Binance API keys + order limits next.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-3">
              <Input
                type="email"
                placeholder="you@example.com"
                autoFocus
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Input
                type="password"
                placeholder="Password (min 8 chars)"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <Input
                type="password"
                placeholder="Confirm password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
              <label className="flex items-start gap-2 text-xs text-muted pt-1">
                <input
                  type="checkbox"
                  className="h-4 w-4 mt-0.5 accent-accent shrink-0"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                />
                <span>
                  I have read and agree to the{" "}
                  <Link to="/terms" target="_blank" className="text-accent underline">
                    Terms of Service & Risk Disclosure
                  </Link>
                  , including the <strong className="text-fg">10% performance fee</strong>{" "}
                  on realized profit.
                </span>
              </label>
              {error && <Alert tone="danger">{error}</Alert>}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isLoading || !email || !password || !acceptedTerms}
              >
                {isLoading ? "Creating account…" : "Create account"}
              </Button>
            </form>
          </CardContent>
        </Card>
        )}
      </div>
      </div>

      {/* Real trading performance — proof from the prod bot.
          Gated on `showPerformance`: only renders when cumulative net P&L
          across Cycles is positive. Keeps a losing tape off the
          signup page entirely. */}
      {showPerformance && (
      <div className="space-y-2">
        <div className="flex items-baseline gap-3">
          <h2 className="text-lg font-semibold">Real trading performance</h2>
          <span className="text-xs text-muted">
            live track record from our production bot
          </span>
        </div>
        <div className="grid lg:grid-cols-2 gap-3">
          <Card>
            <CardHeader>
              <CardTitle>
                Cycles — equity curve
                <span className="text-xs font-normal text-muted ml-2">
                  rolling closed hourly P&L
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {demo.isLoading ? (
                <Skeleton className="h-44 w-full" />
              ) : (
                <EquityCurve positions={(demo.data ?? []) as any} height={180} />
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>
                Cycles — daily P&L
                <span className="text-xs font-normal text-muted ml-2">last 14 days</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {demo.isLoading ? (
                <Skeleton className="h-44 w-full" />
              ) : (
                <DailyPnL positions={(demo.data ?? []) as any} days={14} height={180} />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
      )}
    </div>
  );
}
